import React from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Rating from '@mui/material/Rating';
import Typography from '@mui/material/Typography';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import actions from '../../../redux/actions';
import { RootState } from '../../../redux/store';
import { UserRole } from '../../../types/UserRole';
import { DataTable, DefinitionList, PageContainer, PageSection } from '../../../ui';
import { WorkCategory } from '../../../types/WorkCategory';
import { colors } from '../../../theme/tokens';

const workCategoryLabel: Record<string, string> = {
  [WorkCategory.FindBugsExploratory]: 'Find bugs: exploratory',
  [WorkCategory.FindBugsTestPlan]   : 'Find bugs: test plan',
  [WorkCategory.FeedbackAndRating]  : 'Feedback & star rating',
};

/* Customer → Favorite testers → one tester */
const FavoriteTesterDetails: React.FC = () => {
  const { testerId: testerIdParam } = useParams<{ testerId: string }>();
  const testerId = +testerIdParam;
  const history = useHistory();
  const dispatch = useDispatch();
  const { favoriteTesters, devices, browsers, user, resolution } = useSelector((state: RootState) => state) as any;
  const isMobile = resolution.device === 'mobile' || resolution.device === 'mobile-tablet';

  const tester = favoriteTesters.byId[testerId];

  if (!tester) {
    return null;
  }

  const deviceNames: string[] = (tester.devices || []).map(
    (testerDevice) => devices.models.find((model) => model.id === testerDevice.modelId)?.name || `Unknown device ${testerDevice.modelId}`
  );

  const browserNames: string[] = (tester.browsers || []).map((testerBrowser) => {
    const name = browsers.names.find((el) => el.id === testerBrowser.browserId)?.name || 'Unknown browser';
    const version = browsers.versions.find((el) => el.id === testerBrowser.browserVersionId)?.version || '0.0';
    return `${name} ${version}`;
  });

  const isFavorite = testerId in favoriteTesters.byId;

  const onToggleFavorite = () => {
    if (isFavorite) {
      dispatch(actions.removeTesterFromFavorites(testerId));
      history.push('/account/customer/favorite');
    } else {
      dispatch(actions.addTesterToFavorites(testerId));
    }
  };

  const list = (items: string[]) =>
    items.length ? (
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {items.map((item, i) => <li key={i}>{item}</li>)}
      </Box>
    ) : (
      '—'
    );

  const reviews: any[] = tester.customerReviews || [];
  // From the API (customerDao.listTesterCycleStats): all of the tester's cycles, not only this customer's
  const cycleStats: any[] = tester.cycleStats || [];

  return (
    <PageContainer isMobile={isMobile}>
      {/* Same back link as the tester cycle header (TesterCycleNav) */}
      <Box>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push('/account/customer/favorite')}>
          Favorite testers
        </Button>
      </Box>
      <PageSection padding={isMobile ? 1.5 : 2.5}>
        <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Avatar src={tester.avatarUrl || undefined} alt={tester.firstName} sx={{ width: 88, height: 88 }} />
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography component="h2" sx={{ fontSize: 20, fontWeight: 600, color: colors.titleBlue }}>
              {tester.firstName} {tester.lastName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, my: 0.5 }}>
              <Rating value={Number(tester.rating) || 0} precision={0.25} readOnly size="small" />
              <Typography sx={{ fontSize: 13, color: colors.textGrey }}>{Number(tester.rating || 0).toFixed(2)}</Typography>
            </Box>
            {tester.about && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>{tester.about}</Typography>}
          </Box>
          <Button
            variant={isFavorite ? 'outlined' : 'contained'}
            startIcon={isFavorite ? <StarIcon sx={{ color: '#FFB400' }} /> : <StarBorderIcon />}
            disabled={user.role === UserRole.Viewer}
            onClick={onToggleFavorite}
          >
            {isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          </Button>
        </Box>
      </PageSection>

      <PageSection title="General information" padding={isMobile ? 1.5 : 2.5}>
        <DefinitionList
          isMobile={isMobile}
          items={[
            { label: 'Country', value: tester.country || '—' },
            { label: 'Age', value: tester.age || '—' },
            { label: 'Gender', value: tester.gender || '—' },
            { label: 'Devices', value: list(deviceNames) },
            { label: 'Browsers', value: list(browserNames) },
          ]}
        />
      </PageSection>

      <PageSection title="Test skills" padding={isMobile ? 1.5 : 2.5}>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>Participation across all Testpoint cycles</Typography>
        <DataTable
          rows={cycleStats}
          rowKey={(row) => row.workCategory}
          emptyText="No cycles yet"
          columns={[
            { key: 'type', label: 'Cycle type', render: (row) => workCategoryLabel[row.workCategory] || row.workCategory },
            { key: 'cycles', label: 'Cycles completed', align: 'right', render: (row) => row.cycles },
            { key: 'bugs', label: 'Bugs reported', align: 'right', render: (row) => row.bugs },
          ]}
        />
      </PageSection>

      <PageSection title={`Reviews${reviews.length ? ` (${reviews.length})` : ''}`} padding={isMobile ? 1.5 : 2.5}>
        {reviews.length ? (
          reviews.map((review, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 2, py: 2, borderTop: i ? '1px solid #f0f3f8' : 'none' }}>
              <Avatar src={review.reviewerAvatarUrl || undefined} alt={review.ownerCompanyName} sx={{ width: 40, height: 40 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 600 }}>{review.ownerCompanyName}</Typography>
                  {review.rating && <Rating value={Number(review.rating)} readOnly size="small" />}
                </Box>
                <Typography variant="body2" sx={{ my: 0.75, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{review.text}</Typography>
                <Typography sx={{ fontSize: 12, color: colors.textGrey }}>
                  {review.cycleTitle ? `${review.cycleTitle} · ` : ''}{moment(review.createdAt).format('DD MMM YYYY, HH:mm')}
                </Typography>
              </Box>
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="textSecondary">No reviews yet</Typography>
        )}
      </PageSection>
    </PageContainer>
  );
};

export default FavoriteTesterDetails;
